/**
 * Teacher Hooks
 *
 * React Query hooks for teacher dashboard data.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { teacherService } from "../services";
import type {
    UpdateClassLinksRequest,
    UpdateLevelLinksRequest,
} from "../types/teacher";

/**
 * Hook to fetch teacher dashboard overview
 */
export function useTeacherDashboard() {
  return useQuery({
    queryKey: ["teacher", "dashboard"],
    queryFn: teacherService.getTeacherDashboard,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch teacher's class groups
 */
export function useTeacherClasses(params?: {
  level?: string;
  teacher?: string;
}) {
  return useQuery({
    queryKey: ["teacher", "classes", params],
    queryFn: () => teacherService.getTeacherClasses(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch a single class group
 */
export function useClassGroup(id: string) {
  return useQuery({
    queryKey: ["teacher", "class", id],
    queryFn: () => teacherService.getClassGroup(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to update class links
 */
export function useUpdateClassLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClassLinksRequest }) =>
      teacherService.updateClassLinks(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      queryClient.setQueryData(["teacher", "class", data.id], data);
      // Invalidate user query so students see updated class links
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Hook to update level links
 */
export function useUpdateLevelLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      classId,
      data,
    }: {
      classId: string;
      data: UpdateLevelLinksRequest;
    }) => teacherService.updateLevelLinks(classId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      queryClient.setQueryData(["teacher", "class", data.id], data);
    },
  });
}

/**
 * Hook to fetch teacher's students
 */
export function useTeacherStudents(params?: {
  level?: string;
  classGroup?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ["teacher", "students", params],
    queryFn: () => teacherService.getTeacherStudents(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch teacher calendar
 */
export function useTeacherCalendar(params?: { year?: number; month?: number }) {
  return useQuery({
    queryKey: ["teacher", "calendar", params],
    queryFn: () => teacherService.getTeacherCalendar(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to create schedule change
 */
export function useCreateScheduleChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teacherService.createScheduleChange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "calendar"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "schedule-changes"] });
    },
  });
}

/**
 * Hook to fetch schedule changes
 */
export function useScheduleChanges(params?: {
  classGroup?: string;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ["teacher", "schedule-changes", params],
    queryFn: () => teacherService.getScheduleChanges(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to delete schedule change
 */
export function useDeleteScheduleChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teacherService.deleteScheduleChange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "calendar"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", "schedule-changes"] });
    },
  });
}
